/**
 * Circuit breaker por item da fila de reprodução.
 *
 * Sem isso, um item que falha rápido (CORS, 403, checksum recusado, item sem asset) fazia o
 * loop avançar sem pausa: com todos os itens falhando o player girava a playlist dezenas de
 * vezes por segundo, refazendo download a cada volta. Cada falha consecutiva do mesmo item
 * dobra o tempo em que ele fica de fora (30 s → 10 min), com jitter para a frota não
 * sincronizar as retentativas.
 *
 * A chave inclui checksum e URL: uma agenda com mídia nova sai do cooldown sozinha.
 */
import type { PlayQueueItem } from '@/types/player.types'

export const FAILURE_COOLDOWN_BASE_MS = 30_000
/** Igual ao backoff da verificação em background do asset-cache. */
export const FAILURE_COOLDOWN_MAX_MS = 10 * 60_000
const JITTER_RATIO = 0.2

interface FailureEntry {
  failures: number
  retryAt: number
}

interface TrackerOptions {
  baseMs?: number
  maxMs?: number
  now?: () => number
  random?: () => number
}

export function playbackFailureKey(
  item: Pick<PlayQueueItem, 'assetId' | 'externalUrl' | 'checksum' | 'hlsUrl' | 'assetUrl'>
): string {
  return `${item.assetId ?? item.externalUrl ?? ''}|${item.checksum ?? ''}|${item.hlsUrl ?? item.assetUrl ?? ''}`
}

export class PlaybackFailureTracker {
  private readonly entries = new Map<string, FailureEntry>()
  private readonly baseMs: number
  private readonly maxMs: number
  private readonly now: () => number
  private readonly random: () => number

  constructor(options: TrackerOptions = {}) {
    this.baseMs = options.baseMs ?? FAILURE_COOLDOWN_BASE_MS
    this.maxMs = options.maxMs ?? FAILURE_COOLDOWN_MAX_MS
    // Lambdas, não referências: assim fake timers e spies de Math.random valem para o singleton.
    this.now = options.now ?? (() => Date.now())
    this.random = options.random ?? (() => Math.random())
  }

  /** Registra uma falha e devolve o epoch ms a partir do qual o item pode tocar de novo. */
  recordFailure(key: string): number {
    const failures = (this.entries.get(key)?.failures ?? 0) + 1
    const backoff = Math.min(this.baseMs * 2 ** (failures - 1), this.maxMs)
    const jitter = 1 + (this.random() * 2 - 1) * JITTER_RATIO
    const retryAt = this.now() + Math.round(backoff * jitter)
    this.entries.set(key, { failures, retryAt })
    return retryAt
  }

  recordSuccess(key: string): void {
    this.entries.delete(key)
  }

  isCoolingDown(key: string): boolean {
    const entry = this.entries.get(key)
    return !!entry && entry.retryAt > this.now()
  }

  /** Menor `retryAt` entre os itens ainda em cooldown, ou null se nenhum estiver. */
  earliestRetryAt(): number | null {
    const now = this.now()
    let earliest: number | null = null
    for (const { retryAt } of this.entries.values()) {
      if (retryAt > now && (earliest === null || retryAt < earliest)) earliest = retryAt
    }
    return earliest
  }

  reset(): void {
    this.entries.clear()
  }
}

export const playbackFailureTracker = new PlaybackFailureTracker()
